import type { MeContext, PortfolioNode, WorkspaceNode } from '@vizit/api';
import {
  ACCESS_ROUTES,
  ROLE,
  SCOPE_TYPE,
  SWITCHER_SECTION_KEY,
  SwitcherEntityKind,
  SwitcherVariant,
} from '../constants';
import type { AccessRouteParams, SwitcherItem, SwitcherSection, WorkspaceSwitcherModel } from '../types';
import { findPortfolioById } from './find-portfolio-by-id';
import { findWorkspaceBySlug } from './find-workspace-by-slug';
import { getAccessiblePortfolios, getAccessibleWorkspaces } from './get-accessible';
import { hasGrant } from './grants';
import { isSuperAdmin } from './is-super-admin';
import { resolveOrganizationGridHref } from './route-guards';

function resolveVariant(context: MeContext): SwitcherVariant {
  if (isSuperAdmin(context) || hasGrant(context, ROLE.OrgAdmin, SCOPE_TYPE.Organization, context.organization.id)) {
    return SwitcherVariant.Org;
  }
  const orgPortfolioIds = new Set(context.organization.portfolios.map((portfolio) => portfolio.id));
  const isPortfolioAdminInThisOrg = context.grants.some(
    (grant) =>
      grant.role === ROLE.PortfolioAdmin &&
      grant.scopeType === SCOPE_TYPE.Portfolio &&
      grant.scopeId !== null &&
      orgPortfolioIds.has(grant.scopeId),
  );
  if (isPortfolioAdminInThisOrg) {
    return SwitcherVariant.Portfolio;
  }
  return SwitcherVariant.Workspace;
}

function portfolioHref(portfolio: PortfolioNode): string {
  return ACCESS_ROUTES.portfolioWorkspaces(portfolio.id);
}

function organizationHref(context: MeContext): string | null {
  return resolveOrganizationGridHref(context);
}

function orgItem(context: MeContext, isActive: boolean, isSelected: boolean): SwitcherItem {
  const org = context.organization;
  return {
    id: org.id,
    name: org.name,
    targetHref: organizationHref(context),
    isActive,
    isSelected,
  };
}

function workspaceItem(workspace: WorkspaceNode, activeWorkspaceId: string | null): SwitcherItem {
  const isCurrent = workspace.id === activeWorkspaceId;
  return {
    id: workspace.id,
    name: workspace.name,
    targetHref: ACCESS_ROUTES.workspaceShell(workspace.slug),
    isActive: isCurrent,
    isSelected: isCurrent,
  };
}

function portfolioItem(
  portfolio: PortfolioNode,
  activePortfolioId: string | undefined,
  hasActiveWorkspace: boolean,
): SwitcherItem {
  const isOnActivePath = portfolio.id === activePortfolioId;
  return {
    id: portfolio.id,
    name: portfolio.name,
    targetHref: portfolioHref(portfolio),
    isActive: isOnActivePath && !hasActiveWorkspace,
    isSelected: isOnActivePath,
  };
}

export function buildWorkspaceSwitcherModel(
  context: MeContext,
  routeParams: AccessRouteParams = {},
): WorkspaceSwitcherModel {
  const variant = resolveVariant(context);
  const org = context.organization;
  const activeWorkspace = routeParams.workspaceSlug ? findWorkspaceBySlug(context, routeParams.workspaceSlug) : null;
  const activeWorkspaceId = activeWorkspace?.id ?? null;
  const activePortfolio = findPortfolioById(context, activeWorkspace?.portfolioId ?? routeParams.portfolioId ?? null);
  const sections: SwitcherSection[] = [];

  if (variant === SwitcherVariant.Workspace) {
    const workspaces = getAccessibleWorkspaces(context);
    sections.push({
      key: SWITCHER_SECTION_KEY.Workspaces,
      kind: SwitcherEntityKind.Workspace,
      label: null,
      items: workspaces.map((workspace) => workspaceItem(workspace, activeWorkspaceId)),
    });

    const triggerWorkspace = activeWorkspace ?? workspaces[0];
    return {
      variant,
      trigger: triggerWorkspace ? { id: triggerWorkspace.id, name: triggerWorkspace.name } : null,
      sections,
    };
  }

  if (variant === SwitcherVariant.Org) {
    sections.push({
      key: SWITCHER_SECTION_KEY.Organization,
      kind: SwitcherEntityKind.Organization,
      label: 'Organization',
      items: [orgItem(context, !activePortfolio, true)],
    });
  }

  const portfolios = getAccessiblePortfolios(context);
  if (portfolios.length > 0) {
    sections.push({
      key: SWITCHER_SECTION_KEY.Portfolios,
      kind: SwitcherEntityKind.Portfolio,
      label: 'Portfolios',
      items: portfolios.map((portfolio) => portfolioItem(portfolio, activePortfolio?.id, Boolean(activeWorkspaceId))),
    });
  }

  if (activePortfolio && activePortfolio.workspaces.length > 0) {
    sections.push({
      key: SWITCHER_SECTION_KEY.Workspaces,
      kind: SwitcherEntityKind.Workspace,
      label: `Workspaces - ${activePortfolio.name}`,
      items: activePortfolio.workspaces.map((workspace) => workspaceItem(workspace, activeWorkspaceId)),
    });
  }

  const deepestLevel = activeWorkspace ?? activePortfolio ?? (variant === SwitcherVariant.Org ? org : null);
  const trigger = deepestLevel ? { id: deepestLevel.id, name: deepestLevel.name } : null;

  return { variant, trigger, sections };
}
